/**
 * =============================================================================
 * ADMIN SHELL (ADR-025 §4 IA + blueprint §3.1 — B-1 / BL-ADMIN-WORKBENCH-IA-002)
 * =============================================================================
 *
 * The Find-My-Part-scoped navigation shell: a persistent §4 rail (top tab bar
 * below the tablet breakpoint) over semantic section locations, with ONE
 * section visible at a time. Mechanism per OD-2 (ratified after E3): a typed
 * reducer over `AdminLocation` values — NO router, NO URL writes (the app does
 * not own the iframe URL; hash/pushState measurably create Back-trap history).
 *
 * §3.1 rules implemented here (pinned under M8):
 *   .1 Sections LAZY-MOUNT on first activation, then KEEP-ALIVE: inactive
 *      sections stay mounted but hidden — `display:none` + `inert` +
 *      `aria-hidden` — preserving panel-local state (filters/sort/page/
 *      scroll/drafts). B-4 additionally retains each section's serializable
 *      semantic location; it does not hoist panel implementation state.
 *   .2 The location type carries `questionId` (serializable contract). A bare
 *      rail activation restores the section's last committed location, while
 *      explicit destinations (Back, Overview tiles, media links) replace it.
 *   .3 On section change, focus moves to the section h1 (`tabIndex={-1}`,
 *      `focus({ preventScroll: true })`) — never lost to `<body>`; per-section
 *      scroll is explicitly SAVED on deactivate (at dispatch time, before
 *      layout switches) and RESTORED on activate, and the preventScroll focus
 *      can never clobber the restored offset. The heading is RE-QUERIED by id
 *      at focus time — never a held node (big-design re-creates DOM on
 *      re-render; holding nodes is the documented focus bug class).
 *   .5 Shell-level token-expiry warning (preserved; the tokenless gate lives
 *      one level up in `AdminApp`).
 *   .6 Navigation dispatches locations — never URLs. `initialLocation` is the
 *      host-adapter seam (App-Extension deep links resolve through
 *      `resolveLocation` and are registry-guarded to the landing).
 *
 *   .4 Unsaved-edits surfacing (B-2 — `BL-ADMIN-M1-EXPIRY-DIRTY-GUARD-001`,
 *      the owner-ratified §9 v3.1 deferral out of B-1): the settings cards
 *      report unsaved-edit TRANSITIONS upward via their optional
 *      `onDirtyChange`; the shell ORs them into (a) an unsaved-changes
 *      indicator on the Settings nav item (rail AND narrow tab bar — one
 *      code path) and (b) an extra line on the §3.1.5 expiry warning while
 *      dirty. An INDICATOR, never a blocking prompt — under keep-alive,
 *      leaving Settings discards nothing; the real loss vectors are reload
 *      and token expiry (ext-UX-08). Dirty state lives HERE, so it survives
 *      switch-away-and-back (pinned).
 *
 *   (Rule .7's serialize/resolve round-trip is pinned in `location.test.ts`.)
 *
 * OPEN-DIALOG × SECTION-SWITCH interleaving (keep-alive note): big-design
 * `Modal` portals to <body> ONLY while open (closed modals render null, so a
 * hidden section's closed dialogs cost nothing). While any dialog is open its
 * backdrop covers the rail and its focus trap holds Tab — so switching
 * sections with a dialog open is unreachable through the UI, and the shell
 * deliberately does NOT force-close dialogs on deactivate (that would destroy
 * exactly the draft state rule .1 preserves). B-2's programmatic path (the
 * media-row → parent-question link, via `ShellNavigationContext`) is gated by
 * the SAME mechanism: the link is an in-section control, so any open dialog's
 * backdrop covers it — the interleaving (and the sibling case of an intent
 * replacing an already-open detail modal over a draft) stays unreachable
 * through the UI. A future NON-UI dispatch source (e.g. an App-Extension
 * inbound link) must still resolve this first (B-4's workspace redesign owns
 * the selection choreography).
 * =============================================================================
 */

import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Box, Button, Flex, FlexItem, H1, Text } from '@bigcommerce/big-design';
import { PUBLIC_LAUNCH_LABELS } from '@find-my-part/types';
import { useSession } from '../../hooks/useSession';
import { useIsMobile } from '../../hooks/useIsMobile';
import { usePendingWork } from '../../hooks/usePendingWork';
import { useLaunchStatus } from '../../hooks/useLaunchStatus';
import { useTenantSettingsMutationVersion } from '../../providers/TenantSettingsProvider';
import { TokenExpirationWarning } from '../SessionWarning';
import type { OpenQuestionIntent } from '../Questions/QuestionQueue';
import {
  DEFAULT_ADMIN_LOCATION,
  resolveLocation,
  type AdminFilteredViewIntents,
  type AdminLocation,
  type AdminSectionId,
  type AdminSectionLocations,
} from './location';
import {
  ADMIN_SECTIONS,
  type AdminSectionGroup,
  type SectionRenderContext,
  type SettingsCardId,
} from './sectionRegistry';
import { ShellNavigationContext, type ShellNavigation } from './ShellNavigationContext';
import { tokens } from '../../tokens';
import { StatusChip } from '../common/StatusChip';

const REGISTERED_SECTION_IDS: ReadonlySet<AdminSectionId> = new Set(
  ADMIN_SECTIONS.map((s) => s.id)
);
const LIVE_REGION_REPEAT_DELAY_MS = 100;

/** §4 group order (visual affordance over the registry's rail order). */
const GROUP_ORDER: readonly AdminSectionGroup[] = [
  'OPERATIONS',
  'INSIGHTS',
  'MANAGEMENT',
  'GOVERNANCE',
];

/**
 * Clamp a location to registered sections. Unknown future contract values land
 * on the OD-14 default; Overview is registered without replacing Questions as
 * the default landing.
 */
function toRegisteredLocation(location: AdminLocation): AdminLocation {
  return REGISTERED_SECTION_IDS.has(location.section)
    ? location
    : { ...DEFAULT_ADMIN_LOCATION };
}

interface ShellState {
  location: AdminLocation;
  /** Last explicitly committed semantic location for each mounted section. */
  retainedLocations: Readonly<AdminSectionLocations>;
  /** Sections activated at least once — mounted for keep-alive (§3.1.1). */
  mounted: readonly AdminSectionId[];
  /**
   * B-2 (§3.1.6 / M8): the open-question intent raised by the LAST
   * `questions`-location dispatch that carried a `questionId`. The nonce
   * increments per such dispatch, so `QuestionQueue` (keep-alive-mounted)
   * re-fires on a REPEAT jump to the same question; it consumes each nonce
   * exactly once. Deliberately never cleared — the consumer keys on nonce
   * changes, not presence.
   */
  openQuestionIntent: OpenQuestionIntent | null;
  /** Explicit filtered destinations re-fire even when their wire value repeats. */
  filteredViewIntents: AdminFilteredViewIntents;
}

type ShellAction =
  | {
      type: 'NAVIGATE';
      location: AdminLocation;
      reapplyFilteredView: boolean;
    }
  | { type: 'ACTIVATE_SECTION'; section: AdminSectionId };

/** An intent is raised only by a questions location that names a question. */
function raiseIntent(
  target: AdminLocation,
  previous: OpenQuestionIntent | null
): OpenQuestionIntent | null {
  if (target.section === 'questions' && target.questionId !== undefined) {
    return { questionId: target.questionId, nonce: (previous?.nonce ?? 0) + 1 };
  }
  return previous;
}

function raiseFilteredViewIntent(
  target: AdminLocation,
  previous: AdminFilteredViewIntents
): AdminFilteredViewIntents {
  if (target.section === 'questions' && target.view !== undefined) {
    return {
      ...previous,
      questions: {
        view: target.view,
        nonce: (previous.questions?.nonce ?? 0) + 1,
      },
    };
  }
  if (target.section === 'media' && target.view !== undefined) {
    return {
      ...previous,
      media: {
        view: target.view,
        nonce: (previous.media?.nonce ?? 0) + 1,
      },
    };
  }
  return previous;
}

function shellReducer(state: ShellState, action: ShellAction): ShellState {
  switch (action.type) {
    case 'NAVIGATE': {
      const target = toRegisteredLocation(action.location);
      return {
        location: target,
        retainedLocations: {
          ...state.retainedLocations,
          [target.section]: target,
        } as AdminSectionLocations,
        mounted: state.mounted.includes(target.section)
          ? state.mounted
          : [...state.mounted, target.section],
        openQuestionIntent: raiseIntent(target, state.openQuestionIntent),
        filteredViewIntents: action.reapplyFilteredView
          ? raiseFilteredViewIntent(target, state.filteredViewIntents)
          : state.filteredViewIntents,
      };
    }
    case 'ACTIVATE_SECTION': {
      const target =
        state.retainedLocations[action.section] ??
        ({ section: action.section } as AdminLocation);
      return {
        ...state,
        location: target,
        mounted: state.mounted.includes(target.section)
          ? state.mounted
          : [...state.mounted, target.section],
      };
    }
    default:
      return state;
  }
}

function initShellState(initial: AdminLocation): ShellState {
  const location = toRegisteredLocation(resolveLocation(initial));
  return {
    location,
    retainedLocations: { [location.section]: location },
    mounted: [location.section],
    // The host-adapter seam honours a questionId on the INITIAL location too
    // (App-Extension deep-link readiness): the landing queue resolves it once
    // its first load settles — same fail-safe contract as a dispatched jump.
    openQuestionIntent: raiseIntent(location, null),
    filteredViewIntents: { questions: null, media: null },
  };
}

const sectionContainerId = (id: AdminSectionId): string => `admin-section-${id}`;
const sectionHeadingId = (id: AdminSectionId): string => `admin-section-heading-${id}`;

export interface AdminShellProps {
  /**
   * Host-adapter seam (§3.1.6): a semantic location to land on. Resolved
   * fail-safe and registry-guarded; defaults to the OD-14 Questions landing.
   * v1 has no URL-driven caller — this is the App-Extension-readiness seam
   * and the test seam.
   */
  initialLocation?: AdminLocation;
  /** Server-evaluated B-4 flag; false keeps the legacy detail modal. */
  questionWorkspaceEnabled?: boolean;
}

export function AdminShell({
  initialLocation,
  questionWorkspaceEnabled = false,
}: AdminShellProps): React.ReactElement {
  const { token, isExpiringSoon, minutesUntilExpiry, dismissExpiryWarning, resetExpiryWarning } =
    useSession();
  const isMobile = useIsMobile();

  const [state, dispatch] = useReducer(
    shellReducer,
    initialLocation ?? DEFAULT_ADMIN_LOCATION,
    initShellState
  );
  const activeSection = state.location.section;
  const settingsMutationVersion = useTenantSettingsMutationVersion();
  const pending = usePendingWork(token);
  const launchStatus = useLaunchStatus(token, true, settingsMutationVersion);
  const [pendingLiveMessage, setPendingLiveMessage] = useState('');
  const [launchLiveMessage, setLaunchLiveMessage] = useState('');

  // A persistent live region must transition through an empty state before
  // inserting identical text again. Replacing a same-text keyed child in one
  // commit changed the DOM but NVDA 2026.1.1 did not announce the repeated
  // refresh outcome during the B-4 human gate. Keep the region mounted, clear
  // it immediately for every announcement revision, then insert the message
  // in a later task so assistive technology observes a real empty → message
  // transition. Cancelling the timer prevents stale rapid responses winning.
  useEffect(() => {
    setPendingLiveMessage('');
    if (!pending.announcement) return undefined;
    const timer = window.setTimeout(() => {
      setPendingLiveMessage(pending.announcement);
    }, LIVE_REGION_REPEAT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [pending.announcement, pending.announcementRevision]);

  // The launch projection has the same repeatable-announcement contract.
  // Keep its independent region mounted while clearing and reinserting only
  // the launch message; this preserves source isolation from pending work.
  useEffect(() => {
    setLaunchLiveMessage('');
    if (!launchStatus.announcement) return undefined;
    const timer = window.setTimeout(() => {
      setLaunchLiveMessage(launchStatus.announcement);
    }, LIVE_REGION_REPEAT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [launchStatus.announcement, launchStatus.announcementRevision]);

  // §3.1.3 — per-section scroll offsets, SAVED at dispatch time (before the
  // outgoing section's layout collapses to display:none).
  const scrollPositions = useRef<Map<AdminSectionId, number>>(new Map());
  const activeSectionRef = useRef<AdminSectionId>(activeSection);
  activeSectionRef.current = activeSection;

  const navigationGuardRef = useRef<((proceed: () => void) => void) | null>(null);

  const commitNavigation = useCallback(
    (location: AdminLocation, reapplyFilteredView: boolean): void => {
      // Explicit save-on-deactivate: capture the outgoing section's scroll NOW —
      // after the reducer commits, its container is display:none and the window
      // offset already belongs to the incoming section.
      scrollPositions.current.set(activeSectionRef.current, window.scrollY);
      dispatch({ type: 'NAVIGATE', location, reapplyFilteredView });
    },
    []
  );

  const commitSectionActivation = useCallback((section: AdminSectionId): void => {
    scrollPositions.current.set(activeSectionRef.current, window.scrollY);
    dispatch({ type: 'ACTIVATE_SECTION', section });
  }, []);

  const navigate = useCallback(
    (location: AdminLocation): void => {
      const guard = navigationGuardRef.current;
      if (guard) {
        // Commit directly after approval so the accepted transition cannot
        // recursively re-enter the same guard.
        guard(() => commitNavigation(location, true));
        return;
      }
      commitNavigation(location, true);
    },
    [commitNavigation]
  );

  // Panel-owned semantic state changes (select/Back) still cross the draft
  // guard, but they are not new filtered-destination intents. In particular,
  // selecting a row inside a retained filtered queue must not reset/refetch it.
  const updateLocation = useCallback(
    (location: AdminLocation): void => {
      const guard = navigationGuardRef.current;
      if (guard) {
        guard(() => commitNavigation(location, false));
        return;
      }
      commitNavigation(location, false);
    },
    [commitNavigation]
  );

  const activateSection = useCallback(
    (section: AdminSectionId): void => {
      // Clicking the current rail item changes no location or section. Never
      // route this no-op through a live reply-draft discard boundary.
      if (section === activeSectionRef.current) return;
      const guard = navigationGuardRef.current;
      if (guard) {
        guard(() => commitSectionActivation(section));
        return;
      }
      commitSectionActivation(section);
    },
    [commitSectionActivation]
  );

  const registerNavigationGuard = useCallback(
    (guard: (proceed: () => void) => void): (() => void) => {
      navigationGuardRef.current = guard;
      return () => {
        if (navigationGuardRef.current === guard) navigationGuardRef.current = null;
      };
    },
    []
  );

  // §3.1.6 (B-2): the navigation seam handed to section content — semantic
  // locations only, zero URL writes. Provided by the shell ONLY; the legacy
  // layout renders no provider, so consumers degrade to their pre-shell form.
  const shellNavigation = useMemo<ShellNavigation>(() => ({ navigate }), [navigate]);

  // §3.1.4 (B-2 — BL-ADMIN-M1-EXPIRY-DIRTY-GUARD-001): per-card unsaved-edit
  // state, OR-composed into the Settings nav indicator + the expiry-warning
  // extension. Held HERE (not in the cards) so it survives switch-away-and-
  // back under keep-alive; the cards fire transitions only.
  const [dirtySettingsCards, setDirtySettingsCards] = useState<ReadonlySet<SettingsCardId>>(
    () => new Set()
  );
  const reportSettingsCardDirty = useCallback((card: SettingsCardId, dirty: boolean): void => {
    setDirtySettingsCards((prev) => {
      if (prev.has(card) === dirty) return prev; // no transition — no re-render
      const next = new Set(prev);
      if (dirty) {
        next.add(card);
      } else {
        next.delete(card);
      }
      return next;
    });
  }, []);
  const settingsDirty = dirtySettingsCards.size > 0;

  // §3.1.4 (ext B2-F1): re-arm a dismissed expiry warning when settings become
  // dirty. Without this, an operator who dismissed the CLEAN warning would get
  // NO warning after starting to edit — the exact unsaved-loss-at-expiry case
  // §3.1.4 exists to catch. Fires only on the clean→dirty transition (deps =
  // [settingsDirty]); harmless when the token is not in the raw expiry window
  // (the provider re-gates on that), and re-dismissal while staying dirty
  // sticks (no clean→dirty transition to re-fire it — indicator, not a nag).
  useEffect(() => {
    if (settingsDirty) {
      resetExpiryWarning();
    }
  }, [settingsDirty, resetExpiryWarning]);

  // Shell state handed to each section's render (B-2).
  const renderContext: SectionRenderContext = {
    filteredViewIntents: state.filteredViewIntents,
    openQuestionIntent: state.openQuestionIntent,
    retainedLocations: state.retainedLocations,
    reportSettingsCardDirty,
    location: state.location,
    pending,
    navigate,
    updateLocation,
    questionWorkspaceEnabled,
    registerNavigationGuard,
  };

  // §3.1.3 — after a section change commits: focus the incoming section's h1
  // (preventScroll so focus cannot clobber the restore), then restore its
  // saved scroll (top for a first visit). Skipped on initial mount: landing
  // must not steal focus from the document.
  const previousSectionRef = useRef<AdminSectionId | null>(null);
  const refreshPending = pending.refresh;
  useEffect(() => {
    const previous = previousSectionRef.current;
    previousSectionRef.current = activeSection;
    if (previous === null || previous === activeSection) {
      return;
    }
    if (activeSection === 'overview') {
      void refreshPending();
    }

    // Re-query by id at focus time — never a held node.
    const heading = document.getElementById(sectionHeadingId(activeSection));
    heading?.focus({ preventScroll: true });
    window.scrollTo(0, scrollPositions.current.get(activeSection) ?? 0);
  }, [activeSection, refreshPending]);

  const navItems = (
    <nav aria-label="Find My Part">
      {/* B-6 (M19 region rule): the module label lives INSIDE the nav
          landmark — same rendered position, but no stray content outside
          landmarks. */}
      {!isMobile && (
        <Box padding="xxSmall" marginBottom="xSmall">
          <Text bold marginBottom="none">
            Find My Part
          </Text>
        </Box>
      )}
      {GROUP_ORDER.map((group) => {
        const sections = ADMIN_SECTIONS.filter((s) => s.group === group);
        if (sections.length === 0) return null;
        return (
          <Box key={group} marginBottom={isMobile ? 'none' : 'small'}>
            {!isMobile && (
              <Box padding="xxSmall">
                <Text
                  color="secondary60"
                  marginBottom="none"
                >
                  {group}
                </Text>
              </Box>
            )}
            <Flex
              flexDirection={isMobile ? 'row' : 'column'}
              alignItems={isMobile ? 'center' : 'stretch'}
            >
              {sections.map((section) => {
                const isActive = section.id === activeSection;
                // §3.1.4 (B-2): unsaved-changes affordance on the Settings nav
                // item — an indicator, never a blocking prompt. The dot is a
                // presence mark (never color-alone semantics); the accessible
                // name carries the state. One code path serves the rail AND
                // the narrow tab bar (same Button in both layouts).
                const showUnsavedDot = section.id === 'settings' && settingsDirty;
                const navCount =
                  section.id === 'questions'
                    ? pending.data?.pendingQuestions
                    : section.id === 'media'
                      ? pending.data?.pendingMedia
                      : undefined;
                const navUnit =
                  section.id === 'questions'
                    ? 'questions'
                    : section.id === 'media'
                      ? 'media items'
                      : null;
                const navUnavailable = pending.freshness === 'unavailable' || navCount === undefined;
                return (
                  <FlexItem key={section.id}>
                    <Button
                      variant={isActive ? 'secondary' : 'subtle'}
                      onClick={() => activateSection(section.id)}
                      aria-current={isActive ? 'page' : undefined}
                      aria-label={
                        showUnsavedDot ? `${section.navLabel} — unsaved changes` : undefined
                      }
                      data-testid={`admin-nav-${section.id}`}
                    >
                      {section.navLabel}
                      {navUnit && (
                        <span
                          data-testid={`admin-nav-${section.id}-pending-badge`}
                          aria-label={
                            navUnavailable
                              ? `${navUnit} count unavailable`
                              : `${navCount} ${navUnit}, ${pending.freshness}`
                          }
                          style={{ marginLeft: tokens.dimension.space.sm, fontSize: tokens.typography.scale.caption.fontSize }}
                        >
                          {navUnavailable ? '\u2014' : navCount} {navUnit}
                          {pending.freshness === 'stale' ? ' (stale)' : ''}
                        </span>
                      )}
                      {showUnsavedDot && (
                        <span
                          aria-hidden="true"
                          data-testid="admin-nav-settings-unsaved-dot"
                          style={{
                            display: 'inline-block',
                            width: '8px',
                            height: '8px',
                            marginLeft: tokens.dimension.space.sm,
                            borderRadius: tokens.dimension.radius.pill,
                            // House attention colour (pending-work idiom);
                            // tokenized in B-6 (M19). This aria-hidden 8px dot is
                            // exactly the `.marker` role's intended consumer
                            // (impl-review CL-6): state is conveyed by the
                            // adjacent "— unsaved changes" nav label, so the dot
                            // is decorative and rides `.marker`, not `.text`.
                            backgroundColor: tokens.color.status.attention.marker,
                            verticalAlign: 'middle',
                          }}
                        />
                      )}
                    </Button>
                  </FlexItem>
                );
              })}
            </Flex>
          </Box>
        );
      })}
    </nav>
  );

  const launchLabel =
    launchStatus.freshness === 'fresh' && launchStatus.data
      ? launchStatus.data.publicLaunchMode === 'VISIBLE'
        ? 'Live'
        : PUBLIC_LAUNCH_LABELS[launchStatus.data.publicLaunchMode]
      : null;

  return (
    <Box
      data-testid="admin-shell"
      data-question-workspace-enabled={questionWorkspaceEnabled ? 'true' : 'false'}
    >
      <div
        data-testid="admin-pending-live-region"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
        }}
      >
        {pendingLiveMessage && (
          <span data-testid="admin-pending-live-message">{pendingLiveMessage}</span>
        )}
      </div>
      <div
        data-testid="admin-launch-live-region"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
        }}
      >
        {launchLiveMessage && (
          <span data-testid="admin-launch-live-message">{launchLiveMessage}</span>
        )}
      </div>
      {/* §3.1.5 — shell-level token-expiry warning (preserved from the legacy
          layout). B-2 (§3.1.4 half, BL-ADMIN-M1-EXPIRY-DIRTY-GUARD-001): while
          unsaved settings edits exist the warning additionally says so — the
          AFFORDANCE only. It does not depend on E6 data; any deeper §3.1.5
          gate redesign E6 might motivate (blocking close, save-before-expiry)
          is deliberately NOT built here — E6 (JWT-expiry behaviour per panel)
          is still operator-pending. The legacy layout passes nothing and its
          warning stays byte-identical (§3.5 rollback surface). */}
      {isExpiringSoon && (
        <Box
          role="region"
          aria-label="Session expiry warning"
          padding="medium"
          paddingBottom="none"
        >
          <TokenExpirationWarning
            minutesRemaining={minutesUntilExpiry}
            onDismiss={dismissExpiryWarning}
            hasUnsavedSettings={settingsDirty}
          />
        </Box>
      )}

      <Flex flexDirection={isMobile ? 'column' : 'row'} alignItems="stretch">
        {/* §4 rail — module label (a thin ADR-019 §2.2 seam, NOT an AoAT switcher);
            collapses to a top tab bar below the tablet breakpoint. The mobile bar
            scrolls INSIDE its own container (M13: the page body never scrolls
            sideways at supported widths). */}
        <FlexItem
          style={
            isMobile
              ? { overflowX: 'auto' }
              : { flex: '0 0 200px', borderRight: `1px solid ${tokens.color.border.default}` }
          }
        >
          <Box padding="small">
            {navItems}
          </Box>
            {/* B-6 (M19 region rule): a named complementary landmark — status
                info beside the nav, not navigation itself. */}
            <Box
              role="complementary"
              aria-label="Public status"
              padding="xSmall"
              marginTop="small"
              data-testid="admin-launch-status-chip"
            >
              <Text color="secondary" marginBottom="xxSmall">
                Public status
              </Text>
              <StatusChip
                variant={
                  launchStatus.data?.publicLaunchMode === 'VISIBLE' && launchLabel
                    ? 'success'
                    : launchStatus.data?.publicLaunchMode === 'COMING_SOON' && launchLabel
                      ? 'warning'
                      : 'secondary'
                }
                label={launchLabel ?? '\u2014'}
                ariaLabel={
                  launchLabel
                    ? `Public status: ${launchLabel}`
                    : `Public status unknown; ${launchStatus.freshness}`
                }
              />
              {!launchLabel && (
                <Text color="secondary" marginTop="xxSmall" marginBottom="none">
                  Unknown {'\u2014'} {launchStatus.freshness}
                </Text>
              )}
            </Box>
        </FlexItem>

        {/* Section containers — §3.1.1 keep-alive. Wrapped in the §3.1.6
            navigation provider (B-2) so section content can dispatch semantic
            locations; the flag-OFF legacy layout has NO provider. */}
        {/* B-6 (M19 region rule): the section containers are the page's main
            content — one main landmark, attributes only, no new DOM nodes. */}
        <FlexItem role="main" style={{ flex: '1 1 auto', minWidth: 0 }}>
          <ShellNavigationContext.Provider value={shellNavigation}>
            {ADMIN_SECTIONS.map((section) => {
              if (!state.mounted.includes(section.id)) {
                // Lazy: never mounted until first activation (M14 — a section's
                // fetches cannot fire before an operator opens it).
                return null;
              }
              const isActive = section.id === activeSection;
              return (
                <div
                  key={section.id}
                  id={sectionContainerId(section.id)}
                  data-testid={sectionContainerId(section.id)}
                  aria-hidden={isActive ? undefined : true}
                  style={isActive ? undefined : { display: 'none' }}
                  // React 18 has no `inert` prop typing; the empty-string form
                  // renders the bare attribute (removed when undefined).
                  {...(isActive ? {} : ({ inert: '' } as Record<string, string>))}
                >
                  <Box padding="medium" paddingBottom="none">
                    <H1
                      id={sectionHeadingId(section.id)}
                      tabIndex={-1}
                      marginBottom="none"
                    >
                      {section.heading}
                    </H1>
                  </Box>
                  {section.render(renderContext)}
                </div>
              );
            })}
          </ShellNavigationContext.Provider>
        </FlexItem>
      </Flex>
    </Box>
  );
}
